import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  writeBatch,
  onSnapshot,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { db, storage, auth } from '../config/firebase';
import { logger } from './loggerService';
import {
  Product,
  Order,
  DeliveryPerson,
  Category,
  User,
  UserRole,
  OrderStatus,
  DashboardStats,
  OrderAnalytics,
  UserFormData,
  ServiceArea
} from '../types/models';

// Collection names
export const COLLECTIONS = {
  PRODUCTS: 'products',
  ORDERS: 'orders',
  DELIVERY_PERSONS: 'delivery_persons',
  CATEGORIES: 'categories',
  USERS: 'users',
  SERVICE_AREAS: 'service_areas'
};

// Helper function to convert Firestore timestamp to Date
const convertTimestamp = (timestamp: any): Date => {
  if (timestamp && timestamp.toDate) {
    return timestamp.toDate();
  }
  if (timestamp && typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  return new Date();
};

// Helper function to convert Date to Firestore timestamp
const convertToTimestamp = (date: Date): Timestamp => {
  return Timestamp.fromDate(date);
};

// Helper function to convert Firestore Timestamp to Date
const convertFromTimestamp = (timestamp: Timestamp): Date => {
  return timestamp.toDate();
};

// Product Services
export const productService = {
  // Get all products
  async getProducts(): Promise<Product[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.PRODUCTS),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: convertTimestamp(doc.data().createdAt),
        updatedAt: convertTimestamp(doc.data().updatedAt)
      } as Product));
    } catch (error) {
      logger.error('Error getting products', error, { component: 'ProductService', action: 'getProducts' });
      throw error;
    }
  },

  // Get product by ID
  async getProduct(id: string): Promise<Product | null> {
    try {
      const docRef = doc(db, COLLECTIONS.PRODUCTS, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: convertTimestamp(docSnap.data().createdAt),
          updatedAt: convertTimestamp(docSnap.data().updatedAt)
        } as Product;
      }
      return null;
    } catch (error) {
      logger.error('Error getting product', error, { component: 'ProductService', action: 'getProduct' });
      throw error;
    }
  },

  // Add new product
  async addProduct(product: Omit<Product, 'id'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTS), {
        ...product,
        createdAt: convertToTimestamp(product.createdAt),
        updatedAt: convertToTimestamp(product.updatedAt)
      });
      return docRef.id;
    } catch (error) {
      logger.error('Error adding product', error, { component: 'ProductService', action: 'addProduct' });
      throw error;
    }
  },

  // Update product
  async updateProduct(id: string, product: Partial<Product>): Promise<void> {
    try {
      logger.debug('Firebase updateProduct called', { id, product });
      
      const docRef = doc(db, COLLECTIONS.PRODUCTS, id);
      logger.debug('Document reference created', { id });
      
      // Create update data with proper type conversion
      const updateData: any = {};
      
      // Copy all properties except date fields, filtering out undefined values
      Object.keys(product).forEach(key => {
        const value = product[key as keyof Product];
        if (key !== 'createdAt' && key !== 'updatedAt' && value !== undefined) {
          updateData[key] = value;
        }
      });
      
      // Handle date fields with proper conversion
      updateData.updatedAt = convertToTimestamp(new Date());
      
      if (product.createdAt) {
        updateData.createdAt = convertToTimestamp(product.createdAt);
      }
      
      logger.debug('Calling updateDoc with data', updateData);
      await updateDoc(docRef, updateData);
      logger.debug('Product updated successfully', { id });
    } catch (error: any) {
      logger.error('Error updating product', error, { component: 'ProductService', action: 'updateProduct', data: { id } });
      throw error;
    }
  },

  // Delete product
  async deleteProduct(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.PRODUCTS, id);
      await deleteDoc(docRef);
      logger.firebaseOperation('DELETE', COLLECTIONS.PRODUCTS, true);
    } catch (error) {
      logger.error('Error deleting product', error, { component: 'ProductService', action: 'deleteProduct' });
      logger.firebaseOperation('DELETE', COLLECTIONS.PRODUCTS, false, error);
      throw error;
    }
  },

  // Get products by category
  async getProductsByCategory(category: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.PRODUCTS),
        where('category', '==', category),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: convertTimestamp(doc.data().createdAt),
        updatedAt: convertTimestamp(doc.data().updatedAt)
      } as Product));
    } catch (error) {
      logger.error('Error getting products by category', error, { component: 'ProductService', action: 'getProductsByCategory' });
      throw error;
    }
  },

  // ============================================
  // SERVICE AREA OPERATIONS
  // ============================================

  // Get all service areas
  async getServiceAreas(): Promise<ServiceArea[]> {
    try {
      logger.debug('Firebase getServiceAreas called');
      const q = query(
        collection(db, COLLECTIONS.SERVICE_AREAS),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: convertTimestamp(doc.data().createdAt),
        updatedAt: convertTimestamp(doc.data().updatedAt)
      } as ServiceArea));
    } catch (error) {
      logger.error('Error getting service areas', error);
      throw error;
    }
  },

  // Get active service areas
  async getActiveServiceAreas(): Promise<ServiceArea[]> {
    try {
      logger.debug('Firebase getActiveServiceAreas called');
      const q = query(
        collection(db, COLLECTIONS.SERVICE_AREAS),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: convertTimestamp(doc.data().createdAt),
        updatedAt: convertTimestamp(doc.data().updatedAt)
      } as ServiceArea));
    } catch (error) {
      logger.error('Error getting active service areas', error);
      throw error;
    }
  },

  // Get service area by ID
  async getServiceAreaById(id: string): Promise<ServiceArea | null> {
    try {
      logger.debug('Firebase getServiceAreaById called', { id });
      const docRef = doc(db, COLLECTIONS.SERVICE_AREAS, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: convertTimestamp(docSnap.data().createdAt),
          updatedAt: convertTimestamp(docSnap.data().updatedAt)
        } as ServiceArea;
      }
      return null;
    } catch (error) {
      logger.error('Error getting service area by ID', error);
      throw error;
    }
  },

  // Add new service area
  async addServiceArea(serviceArea: Omit<ServiceArea, 'id'>): Promise<string> {
    try {
      logger.debug('Firebase addServiceArea called', { serviceArea });
      
      const dataToSave = {
        ...serviceArea,
        createdAt: convertToTimestamp(serviceArea.createdAt),
        updatedAt: convertToTimestamp(serviceArea.updatedAt)
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.SERVICE_AREAS), dataToSave);
      logger.info('Service area added successfully', { id: docRef.id });
      return docRef.id;
    } catch (error) {
      logger.error('Error adding service area', error);
      throw error;
    }
  },

  // Update service area
  async updateServiceArea(id: string, serviceArea: Partial<Omit<ServiceArea, 'id'>>): Promise<void> {
    try {
      logger.debug('Firebase updateServiceArea called', { id, serviceArea });
      
      const updateData: any = { ...serviceArea };
      
      // Convert dates to timestamps
      if (updateData.updatedAt) {
        updateData.updatedAt = convertToTimestamp(updateData.updatedAt);
      }
      
      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      const docRef = doc(db, COLLECTIONS.SERVICE_AREAS, id);
      await updateDoc(docRef, updateData);
      logger.info('Service area updated successfully', { id });
    } catch (error) {
      logger.error('Error updating service area', error);
      throw error;
    }
  },

  // Delete service area
  async deleteServiceArea(id: string): Promise<void> {
    try {
      logger.debug('Firebase deleteServiceArea called', { id });
      const docRef = doc(db, COLLECTIONS.SERVICE_AREAS, id);
      await deleteDoc(docRef);
      logger.info('Service area deleted successfully', { id });
    } catch (error) {
      logger.error('Error deleting service area', error);
      throw error;
    }
  }
};

// Order Services
export const orderService = {
  // Get all orders
  async getOrders(): Promise<Order[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.ORDERS),
        orderBy('orderDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id, // Always use Firestore document ID, not the data.id field
          orderDate: convertTimestamp(data.orderDate),
          deliveryDate: convertTimestamp(data.deliveryDate),
          cancelledAt: data.cancelledAt ? convertTimestamp(data.cancelledAt) : undefined,
          statusHistory: data.statusHistory ? data.statusHistory.map((history: any) => ({
            ...history,
            timestamp: convertTimestamp(history.timestamp)
          })) : []
        } as Order;
      });
    } catch (error) {
      throw error;
    }
  },

  // Get order by ID
  async getOrder(id: string): Promise<Order | null> {
    try {
      const docRef = doc(db, COLLECTIONS.ORDERS, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          ...data,
          id: docSnap.id, // Always use Firestore document ID, not the data.id field
          orderDate: convertTimestamp(data.orderDate),
          deliveryDate: convertTimestamp(data.deliveryDate),
          cancelledAt: data.cancelledAt ? convertTimestamp(data.cancelledAt) : undefined,
          statusHistory: data.statusHistory ? data.statusHistory.map((history: any) => ({
            ...history,
            timestamp: convertTimestamp(history.timestamp)
          })) : []
        } as Order;
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update order status with audit trail
  async updateOrderStatus(id: string, status: OrderStatus, notes?: string, updatedBy: string = 'admin', assignedDeliveryAgent?: string, deliveryAgentObject?: any): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.ORDERS, id);
      
      // First, get the current order to know the previous status
      const currentOrderSnap = await getDoc(docRef);
      if (!currentOrderSnap.exists()) {
        throw new Error('Order not found');
      }
      
      const currentOrder = currentOrderSnap.data();
      const previousStatus = currentOrder.status;
      const currentStatusHistory = currentOrder.statusHistory || [];
      
      // Create new status history entry
      const newHistoryEntry = {
        status,
        timestamp: convertToTimestamp(new Date()),
        updatedBy,
        notes: notes || '',
        previousStatus
      };
      
      // Prepare update data
      const updateData: any = {
        status,
        updatedAt: convertToTimestamp(new Date()),
        statusHistory: [...currentStatusHistory, newHistoryEntry]
      };
      
      if (notes) {
        updateData.notes = notes;
      }
      
      if (assignedDeliveryAgent) {
        updateData.assignedDeliveryAgent = assignedDeliveryAgent;
      }
      
      if (deliveryAgentObject) {
        // Clean the delivery agent object to remove any undefined values
        const cleanDeliveryAgent: any = {};
        Object.keys(deliveryAgentObject).forEach(key => {
          if (deliveryAgentObject[key] !== undefined) {
            cleanDeliveryAgent[key] = deliveryAgentObject[key];
          }
        });
        updateData.deliveryAgent = cleanDeliveryAgent;
      }
      
      if (status === OrderStatus.CANCELLED) {
        updateData.cancelledAt = convertToTimestamp(new Date());
      }
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      throw error;
    }
  },

  // Get orders by status
  async getOrdersByStatus(status: OrderStatus): Promise<Order[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.ORDERS),
        where('status', '==', status),
        orderBy('orderDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        orderDate: convertTimestamp(doc.data().orderDate),
        deliveryDate: convertTimestamp(doc.data().deliveryDate),
        cancelledAt: doc.data().cancelledAt ? convertTimestamp(doc.data().cancelledAt) : undefined
      } as Order));
    } catch (error) {
      throw error;
    }
  }
};

// Delivery Person Services
export const deliveryPersonService = {
  // Get all delivery persons
  async getDeliveryPersons(): Promise<DeliveryPerson[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.DELIVERY_PERSONS),
        orderBy('joinDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const result = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          joinDate: convertTimestamp(data.joinDate),
          lastLocationUpdate: data.lastLocationUpdate 
            ? convertTimestamp(data.lastLocationUpdate) 
            : undefined
        } as DeliveryPerson;
      });
      
      return result;
    } catch (error) {
      throw error;
    }
  },

  // Get delivery person by ID
  async getDeliveryPerson(id: string): Promise<DeliveryPerson | null> {
    try {
      const docRef = doc(db, COLLECTIONS.DELIVERY_PERSONS, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
          joinDate: convertTimestamp(docSnap.data().joinDate),
          lastLocationUpdate: docSnap.data().lastLocationUpdate 
            ? convertTimestamp(docSnap.data().lastLocationUpdate) 
            : undefined
        } as DeliveryPerson;
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Add new delivery person
  async addDeliveryPerson(deliveryPerson: Omit<DeliveryPerson, 'id'>): Promise<string> {
    try {
      logger.debug('Firebase addDeliveryPerson called', { deliveryPerson });
      
      const dataToSave = {
        ...deliveryPerson,
        joinDate: convertToTimestamp(deliveryPerson.joinDate),
        lastLocationUpdate: deliveryPerson.lastLocationUpdate 
          ? convertToTimestamp(deliveryPerson.lastLocationUpdate) 
          : null
      };
      
      logger.debug('Calling addDoc to collection', { collection: COLLECTIONS.DELIVERY_PERSONS });
      const docRef = await addDoc(collection(db, COLLECTIONS.DELIVERY_PERSONS), dataToSave);
      
      logger.info('Delivery person added successfully', { id: docRef.id });
      logger.firebaseOperation('CREATE', COLLECTIONS.DELIVERY_PERSONS, true);
      return docRef.id;
    } catch (error: any) {
      logger.error('Error adding delivery person', error, { component: 'DeliveryPersonService', action: 'addDeliveryPerson' });
      logger.firebaseOperation('CREATE', COLLECTIONS.DELIVERY_PERSONS, false, error);
      throw error;
    }
  },

  // Update delivery person
  async updateDeliveryPerson(id: string, deliveryPerson: Partial<DeliveryPerson>): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.DELIVERY_PERSONS, id);
      const updateData: any = { ...deliveryPerson };
      
      if (deliveryPerson.joinDate) {
        updateData.joinDate = convertToTimestamp(deliveryPerson.joinDate);
      }
      if (deliveryPerson.lastLocationUpdate) {
        updateData.lastLocationUpdate = convertToTimestamp(deliveryPerson.lastLocationUpdate);
      }
      
      await updateDoc(docRef, updateData);
      logger.firebaseOperation('UPDATE', COLLECTIONS.DELIVERY_PERSONS, true);
    } catch (error) {
      logger.error('Error updating delivery person', error, { component: 'DeliveryPersonService', action: 'updateDeliveryPerson' });
      logger.firebaseOperation('UPDATE', COLLECTIONS.DELIVERY_PERSONS, false, error);
      throw error;
    }
  },

  // Delete delivery person
  async deleteDeliveryPerson(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.DELIVERY_PERSONS, id);
      await deleteDoc(docRef);
      logger.firebaseOperation('DELETE', COLLECTIONS.DELIVERY_PERSONS, true);
    } catch (error) {
      logger.error('Error deleting delivery person', error, { component: 'DeliveryPersonService', action: 'deleteDeliveryPerson' });
      logger.firebaseOperation('DELETE', COLLECTIONS.DELIVERY_PERSONS, false, error);
      throw error;
    }
  }
};

// Category Services
export const categoryService = {
  // Get all categories
  async getCategories(): Promise<Category[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.CATEGORIES),
        orderBy('sortOrder', 'asc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: convertTimestamp(doc.data().createdAt),
        updatedAt: convertTimestamp(doc.data().updatedAt)
      } as Category));
    } catch (error) {
      logger.error('Error getting categories', error, { component: 'CategoryService', action: 'getCategories' });
      throw error;
    }
  },

  // Add new category
  async addCategory(category: Omit<Category, 'id'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.CATEGORIES), {
        ...category,
        createdAt: convertToTimestamp(category.createdAt),
        updatedAt: convertToTimestamp(category.updatedAt)
      });
      return docRef.id;
    } catch (error) {
      logger.error('Error adding category', error, { component: 'CategoryService', action: 'addCategory' });
      throw error;
    }
  },

  // Update category
  updateCategory: async (id: string, category: Partial<Category>) => {
    try {
      const docRef = doc(db, COLLECTIONS.CATEGORIES, id);
      
      // Create update data with proper type conversion
      const updateData: any = {};
      
      // Copy all properties except date fields
      Object.keys(category).forEach(key => {
        if (key !== 'createdAt' && key !== 'updatedAt') {
          updateData[key] = category[key as keyof Category];
        }
      });
      
      // Handle date fields with proper conversion
      updateData.updatedAt = convertToTimestamp(new Date());
      
      if (category.createdAt) {
        updateData.createdAt = convertToTimestamp(category.createdAt);
      }
      
      await updateDoc(docRef, updateData);
      logger.firebaseOperation('UPDATE', COLLECTIONS.CATEGORIES, true);
    } catch (error) {
      logger.error('Error updating category', error, { component: 'CategoryService', action: 'updateCategory' });
      logger.firebaseOperation('UPDATE', COLLECTIONS.CATEGORIES, false, error);
      throw error;
    }
  },

  // Delete category
  async deleteCategory(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.CATEGORIES, id);
      await deleteDoc(docRef);
      logger.firebaseOperation('DELETE', COLLECTIONS.CATEGORIES, true);
    } catch (error) {
      logger.error('Error deleting category', error, { component: 'CategoryService', action: 'deleteCategory' });
      logger.firebaseOperation('DELETE', COLLECTIONS.CATEGORIES, false, error);
      throw error;
    }
  }
};

// Analytics Services
// Image Upload Services
export const imageService = {
  // Upload image to Firebase Storage
  async uploadImage(file: File, folder: string = 'images'): Promise<string> {
    try {
      logger.debug('Starting image upload', {
        name: file.name,
        size: file.size,
        type: file.type,
        folder
      });
      
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const filePath = `${folder}/${fileName}`;
      
      logger.debug('Image upload path', { filePath });
      const storageRef = ref(storage, filePath);
      
      // Use uploadBytesResumable for better CORS handling
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'uploaded-by': 'admin-dashboard',
          'upload-time': new Date().toISOString()
        }
      };
      
      const snapshot = await uploadBytes(storageRef, file, metadata);
      logger.debug('Upload successful', { bucket: snapshot.metadata.bucket });
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      logger.info('Image uploaded successfully', { url: downloadURL, folder });
      
      return downloadURL;
    } catch (error: any) {
      logger.error('Error uploading image', error, { 
        component: 'ImageService', 
        action: 'uploadImage',
        data: { fileName: file.name, folder }
      });
      
      // Provide specific guidance based on error
      if (error.message?.includes('CORS')) {
        logger.warn('CORS error - Firebase Storage CORS configuration needed');
      } else if (error.code === 'storage/unauthorized') {
        logger.warn('Storage unauthorized - Check Firebase Storage rules');
      } else if (error.code === 'storage/unknown') {
        logger.warn('Storage unknown error - Check if Firebase Storage is enabled');
      }
      
      throw error;
    }
  },

  // Delete image from Firebase Storage
  async deleteImage(imageUrl: string): Promise<void> {
    try {
      if (!imageUrl || !imageUrl.includes('firebase')) {
        logger.debug('Skipping delete - not a Firebase URL', { imageUrl });
        return;
      }
      
      logger.debug('Attempting to delete image', { imageUrl });
      
      // Extract the file path from the download URL
      const url = new URL(imageUrl);
      const pathMatch = url.pathname.match(/\/o\/(.+?)(?:\?|$)/);
      if (!pathMatch) {
        logger.warn('Could not extract file path from URL', { imageUrl });
        return;
      }
      
      const encodedPath = pathMatch[1];
      const filePath = decodeURIComponent(encodedPath);
      logger.debug('Extracted file path for deletion', { filePath });
      
      const imageRef = ref(storage, filePath);
      await deleteObject(imageRef);
      logger.info('Image deleted successfully', { filePath });
    } catch (error: any) {
      logger.error('Error deleting image', error, { component: 'ImageService', action: 'deleteImage' });
      
      // Don't throw error for delete operations to prevent blocking category deletion
      if (error.code === 'storage/object-not-found') {
        logger.debug('Image already deleted or not found, continuing');
      } else if (error.code === 'storage/unauthorized') {
        logger.warn('Unauthorized to delete image - check storage rules');
      } else {
        logger.warn('Unknown storage error', { code: error.code, message: error.message });
      }
      
      // Don't re-throw the error to prevent blocking category deletion
    }
  }
};

export const analyticsService = {
  // Get dashboard stats
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = convertToTimestamp(today);

      // Get total counts
      const [productsSnap, ordersSnap, deliveryPersonsSnap] = await Promise.all([
        getDocs(collection(db, COLLECTIONS.PRODUCTS)),
        getDocs(collection(db, COLLECTIONS.ORDERS)),
        getDocs(collection(db, COLLECTIONS.DELIVERY_PERSONS))
      ]);

      const orders = ordersSnap.docs.map(doc => doc.data() as Order);
      const deliveryPersons = deliveryPersonsSnap.docs.map(doc => doc.data() as DeliveryPerson);

      // Calculate stats
      const totalRevenue = orders.reduce((sum, order) => sum + order.totalAmount, 0);
      const todayOrders = orders.filter(order => {
        const orderDate = convertTimestamp(order.orderDate);
        return orderDate >= today;
      });
      const todayRevenue = todayOrders.reduce((sum, order) => sum + order.totalAmount, 0);
      const pendingOrders = orders.filter(order => order.status === OrderStatus.PENDING).length;
      const activeDeliveryPersons = deliveryPersons.filter(dp => dp.isActive && dp.status !== 'offline').length;

      return {
        totalOrders: orders.length,
        totalRevenue,
        totalProducts: productsSnap.size,
        totalDeliveryPersons: deliveryPersons.length,
        todayOrders: todayOrders.length,
        todayRevenue,
        pendingOrders,
        activeDeliveryPersons
      };
    } catch (error) {
      throw error;
    }
  },

  // Get order analytics for the last 30 days
  async getOrderAnalytics(): Promise<OrderAnalytics[]> {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const q = query(
        collection(db, COLLECTIONS.ORDERS),
        where('orderDate', '>=', convertToTimestamp(thirtyDaysAgo)),
        orderBy('orderDate', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      const orders = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        orderDate: convertTimestamp(doc.data().orderDate)
      } as Order));

      // Group orders by date
      const analytics: { [key: string]: { orders: number; revenue: number } } = {};
      
      orders.forEach(order => {
        const dateKey = order.orderDate.toISOString().split('T')[0];
        if (!analytics[dateKey]) {
          analytics[dateKey] = { orders: 0, revenue: 0 };
        }
        analytics[dateKey].orders++;
        analytics[dateKey].revenue += order.totalAmount;
      });

      // Convert to array and fill missing dates
      const result: OrderAnalytics[] = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateKey = date.toISOString().split('T')[0];
        
        result.push({
          date: dateKey,
          orders: analytics[dateKey]?.orders || 0,
          revenue: analytics[dateKey]?.revenue || 0
        });
      }

      return result;
    } catch (error) {
      throw error;
    }
  }
};

// User Services with Role-based Authentication
export const userService = {
  // Get all users
  async getUsers(): Promise<User[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.USERS),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: convertTimestamp(doc.data().createdAt),
        updatedAt: convertTimestamp(doc.data().updatedAt)
      } as User));
    } catch (error) {
      throw error;
    }
  },

  // Get user by ID
  async getUser(id: string): Promise<User | null> {
    try {
      const docRef = doc(db, COLLECTIONS.USERS, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: convertTimestamp(docSnap.data().createdAt),
          updatedAt: convertTimestamp(docSnap.data().updatedAt)
        } as User;
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Get user by email
  async getUserByEmail(email: string): Promise<User | null> {
    try {
      const q = query(
        collection(db, COLLECTIONS.USERS),
        where('email', '==', email),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
          createdAt: convertTimestamp(doc.data().createdAt),
          updatedAt: convertTimestamp(doc.data().updatedAt)
        } as User;
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Get users by role
  async getUsersByRole(role: UserRole): Promise<User[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.USERS),
        where('role', '==', role),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: convertTimestamp(doc.data().createdAt),
        updatedAt: convertTimestamp(doc.data().updatedAt)
      } as User));
    } catch (error) {
      throw error;
    }
  },

  // Create new user with Firebase Auth and Firestore
  async createUser(userData: UserFormData): Promise<string> {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        userData.email, 
        userData.password
      );
      const authUser = userCredential.user;
      
      // Update display name in Firebase Auth
      await updateProfile(authUser, {
        displayName: userData.name
      });
      
      // Create user document in Firestore
      const userDoc: any = {
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        role: userData.role,
        profileImage: userData.profileImage || '',
        addresses: [],
        isActive: userData.isActive,
        createdAt: convertToTimestamp(new Date()),
        updatedAt: convertToTimestamp(new Date())
      };
      
      // Only add optional fields if they have values (avoid undefined)
      if (userData.role === UserRole.ADMIN) {
        userDoc.adminPermissions = ['all'];
      }
      
      // deliveryPersonId will be added later via linkDeliveryPerson()
      
      // Use the Firebase Auth UID as the document ID
      const docRef = doc(db, COLLECTIONS.USERS, authUser.uid);
      await setDoc(docRef, userDoc);
      
      return authUser.uid;
    } catch (error: any) {
      throw new Error(`Failed to create user: ${error.message}`);
    }
  },

  // Update user
  async updateUser(id: string, userData: Partial<User>): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.USERS, id);
      const updateData: any = { ...userData };
      
      if (userData.createdAt) {
        updateData.createdAt = convertToTimestamp(userData.createdAt);
      }
      if (userData.updatedAt) {
        updateData.updatedAt = convertToTimestamp(userData.updatedAt);
      } else {
        updateData.updatedAt = convertToTimestamp(new Date());
      }
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      throw error;
    }
  },

  // Delete user (soft delete by setting isActive to false)
  async deleteUser(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.USERS, id);
      await updateDoc(docRef, {
        isActive: false,
        updatedAt: convertToTimestamp(new Date())
      });
    } catch (error) {
      throw error;
    }
  },

  // Link delivery person to user account
  async linkDeliveryPerson(userId: string, deliveryPersonId: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.USERS, userId);
      await updateDoc(docRef, {
        deliveryPersonId: deliveryPersonId,
        updatedAt: convertToTimestamp(new Date())
      });
    } catch (error) {
      throw error;
    }
  },

  // Get multiple users by IDs
  async getUsersByIds(userIds: string[]): Promise<User[]> {
    try {
      if (userIds.length === 0) return [];
      
      const users: User[] = [];
      
      // Firestore 'in' queries are limited to 10 items, so we batch them
      const batches = [];
      for (let i = 0; i < userIds.length; i += 10) {
        const batch = userIds.slice(i, i + 10);
        batches.push(batch);
      }
      
      for (const batch of batches) {
        const q = query(
          collection(db, COLLECTIONS.USERS),
          where('__name__', 'in', batch.map(id => doc(db, COLLECTIONS.USERS, id)))
        );
        const querySnapshot = await getDocs(q);
        
        querySnapshot.docs.forEach(doc => {
          users.push({
            id: doc.id,
            ...doc.data(),
            createdAt: convertTimestamp(doc.data().createdAt),
            updatedAt: convertTimestamp(doc.data().updatedAt)
          } as User);
        });
      }
      
      return users;
    } catch (error) {
      throw error;
    }
  },

  // Create admin user with phone number (for OTP authentication)
  async createAdminUser(name: string, mobile: string, email?: string): Promise<string> {
    try {
      
      // Check if user with this mobile already exists
      const existingUser = await this.getUserByPhone(mobile);
      if (existingUser) {
        throw new Error('User with this mobile number already exists');
      }
      
      // Create user document in Firestore
      const userData = {
        name,
        email: email || `${mobile}@shetkari.com`, // Generate email if not provided
        mobile,
        role: UserRole.ADMIN,
        isActive: true,
        createdAt: convertToTimestamp(new Date()),
        updatedAt: convertToTimestamp(new Date())
      };
      
      const docRef = await addDoc(collection(db, COLLECTIONS.USERS), userData);
      
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get user by phone number
  async getUserByPhone(mobile: string): Promise<User | null> {
    try {
      const q = query(
        collection(db, COLLECTIONS.USERS),
        where('mobile', '==', mobile),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
          createdAt: convertTimestamp(doc.data().createdAt),
          updatedAt: convertTimestamp(doc.data().updatedAt)
        } as User;
      }
      return null;
    } catch (error) {
      throw error;
    }
  }
};
